import { Link } from 'react-router-dom';
import { site } from '@shared/site.config';
import { useTitle } from '@/lib/useTitle';
import { useJsonLd } from '@/lib/useJsonLd';
import { faqPageJsonLd } from '@/lib/seo';
import DimensionLine from '@/components/DimensionLine';

export const meta = {
  path: '/selling-tenanted-commercial-property-ontario',
  title: 'Selling a Tenanted Commercial Property in Ontario — Owner Guide',
  description:
    'An Ontario commercial property owner guide to preparing a tenanted building for sale: leases, rent roll, options, recoveries, buyer due diligence, tenant communication and closing preparation.',
};

const FAQS = [
  {
    q: 'Can I sell a commercial property while it is leased to a tenant?',
    a: 'Commercial properties are routinely sold with tenants in place. The lease, amendments and applicable law need to be reviewed so the sale process, access, notices and closing obligations match the actual tenancy.',
  },
  {
    q: 'Does selling the property let the owner change or end the commercial lease?',
    a: 'Do not assume a sale lets you rewrite or end the lease. Ontario commercial leases are highly contract-specific, and the signed lease can govern many rights and obligations. Have your lawyer review the document before making promises about vacancy or possession.',
  },
  {
    q: 'What documents will a buyer usually want to review?',
    a: 'Expect requests for the executed lease and amendments, rent roll, deposit information, arrears history, operating-cost reconciliations, notices, options, insurance information and other property records relevant to the income stream and tenancy.',
  },
  {
    q: 'What is an estoppel certificate?',
    a: 'It is a tenant statement confirming specified facts about the lease and tenancy. Whether one can be required, what it must say and when it is delivered depends on the lease and transaction documents, so counsel should review the requirement.',
  },
];

const prepItems = [
  'Executed lease, amendments, renewals and side letters',
  'Current rent roll and payment history',
  'Security deposit, prepaid rent or letter-of-credit details',
  'Operating-cost / TMI reconciliations and current estimates',
  'Tenant notices, defaults, disputes or outstanding landlord work',
  'Renewal options, termination rights, expansion rights and purchase rights',
  'Insurance certificates and material property service contracts',
  'Recent operating statements and material capital-work records',
];

export default function SellingTenantedCommercialPropertyOntario() {
  useTitle(meta.title, meta.description);
  useJsonLd('faq-selling-tenanted-commercial-property', faqPageJsonLd(FAQS));
  useJsonLd('article-selling-tenanted-commercial-property', {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: 'Selling a Tenanted Commercial Property in Ontario',
    description: meta.description,
    author: { '@type': 'Person', name: site.agent.name },
    publisher: { '@type': 'Organization', name: site.brand.name },
    mainEntityOfPage: `https://pinnaclekwc.com${meta.path}`,
    about: ['Commercial real estate', 'Commercial tenancy', 'Ontario', 'Property sale'],
  });

  return (
    <>
      <section className="border-b border-medline bg-grid-sheet">
        <div className="mx-auto max-w-7xl px-5 py-16 sm:px-8 sm:py-24">
          <p className="annot">Owner guide — Ontario commercial property</p>
          <h1 className="mt-4 max-w-5xl font-display text-[38px] font-semibold leading-[1.03] tracking-tight sm:text-[58px]">
            Selling a tenanted commercial property in Ontario.
          </h1>
          <p className="mt-6 max-w-3xl text-[17px] leading-relaxed text-annot">
            A buyer is not only buying the building — they are underwriting the lease, tenant and future income stream. Preparing those facts before going to market can reduce surprises after an offer arrives.
          </p>
          <div className="mt-9 flex flex-wrap gap-3">
            <Link to="/commercial-property-value-calculator" className="btn-primary">Estimate value from NOI</Link>
            <Link to="/contact?interest=buying-selling" className="btn-ghost">Discuss a tenanted property</Link>
          </div>
        </div>
      </section>

      <section className="bg-sheet">
        <div className="mx-auto max-w-7xl px-5 py-16 sm:px-8">
          <DimensionLine label="Start with the lease, not the listing" className="mb-10" />
          <div className="grid gap-10 lg:grid-cols-[1.25fr_0.75fr]">
            <div className="space-y-5 text-[16px] leading-relaxed text-graphite-600">
              <p>
                Ontario's Commercial Tenancies Act outlines the commercial landlord-tenant relationship, but the province also warns that a signed commercial lease agreement may take precedence over the Act on important points. That makes the actual lease package the first document to review before marketing a tenanted property.
              </p>
              <p>
                Do not promise a buyer vacant possession, a rent increase, early termination, unrestricted showing access or any other tenant outcome until the lease and legal position have been checked. Commercial leases can contain highly specific notice, access, renewal, assignment, purchase-right and operating-cost provisions.
              </p>
              <p>
                If the lease package is incomplete or the owner and tenant disagree about a material term, it is usually better to identify that issue before buyers price the uncertainty themselves.
              </p>
            </div>
            <div className="h-fit border-l-2 border-redline bg-redline-100/40 px-6 py-5">
              <p className="font-mono text-[10px] uppercase tracking-wider2 text-redline-600">Legal line</p>
              <p className="mt-2 text-[14px] leading-relaxed text-graphite-600">
                This page is a transaction-preparation guide, not legal advice. Commercial tenancy rights are lease-specific. Use an Ontario commercial real-estate lawyer for the lease, agreement of purchase and sale, notices and closing documents.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="border-t border-medline">
        <div className="mx-auto max-w-7xl px-5 py-16 sm:px-8">
          <DimensionLine label="Seller document room" className="mb-10" />
          <h2 className="max-w-3xl font-display text-[30px] leading-tight sm:text-[38px]">Build the buyer file before the buyer asks for it.</h2>
          <p className="mt-4 max-w-3xl text-[15px] leading-relaxed text-annot">
            Exact requests vary by asset and transaction, but these are common items worth organizing early.
          </p>
          <div className="mt-9 grid gap-px border border-medline bg-medline sm:grid-cols-2">
            {prepItems.map((item, i) => (
              <div key={item} className="flex gap-5 bg-sheet px-6 py-5">
                <span className="sheet-no pt-0.5">{String(i + 1).padStart(2, '0')}</span>
                <p className="text-[15px] leading-relaxed text-graphite-600">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-medline bg-sheet">
        <div className="mx-auto max-w-7xl px-5 py-16 sm:px-8">
          <DimensionLine label="What buyers actually underwrite" className="mb-10" />
          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-4">
            <div>
              <p className="sheet-no">01</p>
              <h2 className="mt-3 font-display text-[21px]">Income durability</h2>
              <p className="mt-3 text-[14px] leading-relaxed text-annot">Rent, escalations, recoveries, arrears, security and how much lease term remains.</p>
            </div>
            <div>
              <p className="sheet-no">02</p>
              <h2 className="mt-3 font-display text-[21px]">Tenant risk</h2>
              <p className="mt-3 text-[14px] leading-relaxed text-annot">Credit quality, operating history, guarantees and whether occupancy is essential to the tenant's business.</p>
            </div>
            <div>
              <p className="sheet-no">03</p>
              <h2 className="mt-3 font-display text-[21px]">Future control</h2>
              <p className="mt-3 text-[14px] leading-relaxed text-annot">Renewal options, expansion rights, termination rights, exclusivity and any purchase or first-refusal rights in the documents.</p>
            </div>
            <div>
              <p className="sheet-no">04</p>
              <h2 className="mt-3 font-display text-[21px]">Capital exposure</h2>
              <p className="mt-3 text-[14px] leading-relaxed text-annot">Landlord work, repair obligations, deferred maintenance and costs that cannot be recovered from the tenant.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="border-t border-medline">
        <div className="mx-auto max-w-7xl px-5 py-16 sm:px-8">
          <DimensionLine label="Showing & tenant communication" className="mb-10" />
          <div className="max-w-4xl space-y-5 text-[16px] leading-relaxed text-graphite-600">
            <p>
              A tenanted sale needs a showing plan. Review the lease for access and notice provisions, decide who communicates with the tenant and avoid creating unnecessary uncertainty for a tenant whose cooperation may matter during due diligence.
            </p>
            <p>
              Where confidentiality matters, the marketing sequence can be staged: qualify buyers first, release sensitive lease information under the transaction's confidentiality process, and coordinate physical access rather than treating an occupied commercial property like a vacant listing.
            </p>
            <p>
              Buyers and lenders may also ask for an estoppel certificate or similar tenant confirmation. Whether one is required and the form/timing of delivery depends on the lease and deal documents; this should be coordinated with the owner's lawyer rather than improvised late in due diligence.
            </p>
          </div>
        </div>
      </section>

      <section className="border-t border-medline bg-table">
        <div className="mx-auto max-w-7xl px-5 py-16 sm:px-8">
          <DimensionLine label="Questions owners ask" className="mb-10" />
          <div className="max-w-4xl">
            {FAQS.map((f, i) => (
              <div key={f.q} className="grid grid-cols-[auto_1fr] gap-6 border-t border-hairline py-6 last:border-b last:border-hairline">
                <span className="sheet-no pt-1">{String(i + 1).padStart(2, '0')}</span>
                <div>
                  <h2 className="font-display text-[19px] leading-snug">{f.q}</h2>
                  <p className="mt-2 text-[15px] leading-relaxed text-graphite-600">{f.a}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-medline bg-sheet">
        <div className="mx-auto max-w-7xl px-5 py-16 sm:px-8">
          <DimensionLine label="Primary Ontario references" className="mb-8" />
          <p className="max-w-3xl text-[14px] leading-relaxed text-annot">
            For the legal framework, see Ontario's Commercial Tenancies Act and the province's commercial-property renting guidance. RECO also publishes a commercial information guide explaining the real-estate representation framework in Ontario.
          </p>
          <div className="mt-5 flex flex-wrap gap-x-7 gap-y-3 text-[13px]">
            <a href="https://www.ontario.ca/laws/statute/90l07" target="_blank" rel="noreferrer" className="text-redline hover:text-redline-600">Ontario Commercial Tenancies Act</a>
            <a href="https://www.ontario.ca/page/renting-commercial-property-ontario" target="_blank" rel="noreferrer" className="text-redline hover:text-redline-600">Ontario commercial tenancy guidance</a>
            <a href="https://www.reco.on.ca/consumers/things-you-need-to-know/reco-information-guide/" target="_blank" rel="noreferrer" className="text-redline hover:text-redline-600">RECO information guide</a>
          </div>

          <div className="mt-12 flex flex-wrap items-end justify-between gap-8 border-t border-hairline pt-10">
            <div>
              <p className="annot">Waterloo Region & Guelph</p>
              <h2 className="mt-2 max-w-2xl font-display text-[30px] leading-tight">Have a tenanted commercial property you may sell?</h2>
            </div>
            <div className="flex flex-wrap gap-3">
              <Link to="/contact?interest=buying-selling" className="btn-primary">Talk through the property</Link>
              <Link to="/commercial-property-value-calculator" className="btn-ghost">Run the NOI value calculator</Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
